/**
 * Impersonation Session Storage
 *
 * THE single place that knows where an impersonated session lives and what
 * makes it valid. Everything else (storageService.getAuthToken, the modals that
 * mint a session, ImpersonationContext) goes through these functions so the
 * question "which token do requests actually carry?" has exactly one answer.
 *
 * Two distinct slots, on purpose:
 *  - ACTIVE  (four keys): this tab IS the impersonated session.
 *  - HANDOFF (one key)  : a session was minted here and belongs to the tab that
 *    is about to open. `window.open` hands the new tab a COPY of sessionStorage,
 *    so if the minting tab wrote the ACTIVE keys, the admin's own panel would
 *    start sending the impersonated bearer too. The destination promotes the
 *    hand-off into ACTIVE and deletes it; the opener deletes its own copy.
 */

import { STORAGE_KEYS } from '@/shared/utils/constants'

/** The person whose identity the session borrows, as shown in the banner. */
export interface ImpersonatedUserSummary {
  id: string
  email: string
  role: string
  tenantId: string | null
  tenantName: string | null
}

/** Everything needed to BE an impersonated session. */
export interface ImpersonationSessionSnapshot {
  token: string
  user: ImpersonatedUserSummary
  originalAdminId: string
  /** ISO-8601 instant after which the token is dead server-side. */
  expiresAt: string
}

const readSessionItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    // Private-mode browsers throw on sessionStorage access. No session, then.
    return null
  }
}

const writeSessionItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Nothing to recover: the caller surfaces the failure through its own path.
  }
}

const removeSessionItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // Same as above — best effort.
  }
}

const isStillValid = (expiresAt: string): boolean => {
  const expiry = new Date(expiresAt).getTime()
  return Number.isFinite(expiry) && expiry > Date.now()
}

const parseUserSummary = (raw: string): ImpersonatedUserSummary | null => {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const candidate = parsed as Partial<ImpersonatedUserSummary>
    if (typeof candidate.id !== 'string' || typeof candidate.email !== 'string') return null
    return {
      id: candidate.id,
      email: candidate.email,
      role: typeof candidate.role === 'string' ? candidate.role : 'user',
      tenantId: candidate.tenantId ?? null,
      tenantName: candidate.tenantName ?? null,
    }
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────
// ACTIVE SESSION
// ─────────────────────────────────────────────────────────────

/**
 * The impersonated session this tab is running, or `null` when there is none,
 * it already expired, or the stored data is unreadable. An expired or corrupt
 * session is ERASED here rather than left to rot: leaving a dead bearer behind
 * would keep `getActiveImpersonationToken` answering with a token the server
 * rejects, and every request would 401 instead of falling back to the admin's
 * own credentials.
 */
export const readActiveImpersonationSession = (): ImpersonationSessionSnapshot | null => {
  const token = readSessionItem(STORAGE_KEYS.IMPERSONATION_TOKEN)
  const userJson = readSessionItem(STORAGE_KEYS.IMPERSONATION_USER)
  const originalAdminId = readSessionItem(STORAGE_KEYS.IMPERSONATION_ADMIN_ID)
  const expiresAt = readSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRES_AT)

  if (!token || !userJson || !originalAdminId || !expiresAt) return null

  const user = parseUserSummary(userJson)
  if (!user || !isStillValid(expiresAt)) {
    clearActiveImpersonationSession()
    return null
  }

  return { token, user, originalAdminId, expiresAt }
}

/** The bearer every request must carry while impersonating. */
export const getActiveImpersonationToken = (): string | null =>
  readActiveImpersonationSession()?.token ?? null

export const writeActiveImpersonationSession = (session: ImpersonationSessionSnapshot): void => {
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_TOKEN, session.token)
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_USER, JSON.stringify(session.user))
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_ADMIN_ID, session.originalAdminId)
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRES_AT, session.expiresAt)
}

export const clearActiveImpersonationSession = (): void => {
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_TOKEN)
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_USER)
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_ADMIN_ID)
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRES_AT)
}

// ─────────────────────────────────────────────────────────────
// HAND-OFF (minting tab → destination tab)
// ─────────────────────────────────────────────────────────────

export const writePendingImpersonationHandoff = (session: ImpersonationSessionSnapshot): void => {
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_HANDOFF, JSON.stringify(session))
}

export const clearPendingImpersonationHandoff = (): void => {
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_HANDOFF)
}

/**
 * Consume the hand-off: read it, delete it, return it. Deleting on read is the
 * point — a hand-off must be promoted exactly once, by the tab that lands on
 * `?impersonate=true`.
 */
export const takePendingImpersonationHandoff = (): ImpersonationSessionSnapshot | null => {
  const raw = readSessionItem(STORAGE_KEYS.IMPERSONATION_HANDOFF)
  clearPendingImpersonationHandoff()
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const candidate = parsed as Partial<ImpersonationSessionSnapshot>
    if (
      typeof candidate.token !== 'string' ||
      typeof candidate.originalAdminId !== 'string' ||
      typeof candidate.expiresAt !== 'string' ||
      typeof candidate.user !== 'object' ||
      candidate.user === null
    ) {
      return null
    }
    const user = parseUserSummary(JSON.stringify(candidate.user))
    if (!user || !isStillValid(candidate.expiresAt)) return null
    return {
      token: candidate.token,
      user,
      originalAdminId: candidate.originalAdminId,
      expiresAt: candidate.expiresAt,
    }
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────
// EXPIRY NOTICE
// ─────────────────────────────────────────────────────────────

/**
 * Record that the session died of old age. Set BEFORE the reload that drops the
 * impersonated identity, so the admin is told why the screen changed under them
 * instead of being handed their own tenant's data without a word.
 */
export const markImpersonationExpired = (): void => {
  writeSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRED_NOTICE, 'true')
}

/** Read-and-clear: the notice is shown once, then gone. */
export const takeImpersonationExpiredNotice = (): boolean => {
  const flag = readSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRED_NOTICE)
  removeSessionItem(STORAGE_KEYS.IMPERSONATION_EXPIRED_NOTICE)
  return flag === 'true'
}
